import { useState, useEffect, useMemo, useCallback } from "react";
import { AddLayout } from "../../layouts";
import { Paper, Tab, Tabs, TabPanel } from "../../components";
import {
  ConfiguracaoCadastroTab,
  ConfiguracaoComprasTab,
  ConfiguracaoFinanceiroTab,
  ConfiguracaoFiscalTab,
  ConfiguracaoGeralTab,
  ConfiguracaoImpressaoTab,
  ConfiguracaoPdvTab,
  ConfiguracaoServicosTab,
  ConfiguracaoVendaTab,
  IntegracoesTab,
} from "./components/tabs";
import {
  ConfiguracoesService,
  notification,
  SessionService,
} from "../../services";
import { responseErros, unformatPrice, voltar } from "../../utils";
import { useImportContext } from "../../contexts/import.context";
import { ConfiguracoesEntity } from "./entities";
import { configuracoesValidator } from "./middlewares";
import { InputErros, PermisoesHelper } from "../../helpers";

const Configuracoes = () => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("configuracoes-visualizar");
  const { initialCollapses } = useImportContext();
  const configuracoesService = new ConfiguracoesService();
  const sessionService = new SessionService();
  const dadosEmpresa = sessionService.getEmpresa();
  const [loading, setLoading] = useState(false);
  const [listaPagamentoPadrao, setListaPagamentoPadrao] = useState([]);
  const [configuracao, setConfiguracao] = useState({
    impressaoDiretaPdv: false,
    limiteCredito: "AVISAR",
    inadimplencia: "AVISAR",
    modalidadeComissao: "FATURAMENTO",
  });
  const [abaValue, setAbaValue] = useState(0);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    buscaConfiguracoes();
  }, []);

  const buscaConfiguracoes = async () => {
    const result = await configuracoesService.get();
    if (!result.isAxiosError && result.data.count) {
      const data = result.data.rows[0];
      setConfiguracao(data);
      inserirValoresListaPagamentoPadrao(
        data.meioPagamentosPadroesConfiguracoes,
      );
    } else {
      responseErros(result);
    }
  };

  const handleSubmit = () => {
    setInputErro([]);
    configuracoesValidator
      .validate(configuracao, { abortEarly: false })
      .then(() => {
        atualizar();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  async function atualizar() {
    setLoading(true);
    const configuracoesEntity = new ConfiguracoesEntity(configuracao);
    configuracoesEntity.setPagamentosPadroes(listaPagamentoPadrao);
    const result = await configuracoesService.atualizar(
      configuracao.id,
      configuracoesEntity,
    );
    setLoading(false);
    if (!result.isAxiosError) {
      buscaConfiguracoes();
      notification.alteracaoSucesso();
      initialCollapses();
      voltar();
    } else {
      responseErros(result);
    }
  }

  const onChangeSwitch = useCallback((event) => {
    const { name, checked } = event.target;
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }, []);

  const onChangeDate = useCallback((name, date) => {
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: date,
    }));
  }, []);

  const onChangeAutocomplete = useCallback((name, value) => {
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: value ? value.id : undefined,
    }));
  }, []);

  const onChangeValor = useCallback((name, valor) => {
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: valor,
    }));
  }, []);

  const onChange = useCallback((event) => {
    const { name, value } = event.target;
    let valor = value;
    if (name === "carencia") {
      valor = value.replace(/\D/g, "");
    } else if (name === "emailPersonalizado") {
      valor = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: valor,
    }));
  }, []);

  const onChangeRadio = useCallback((event) => {
    const { name, value } = event.target;
    const valor = value === "true";
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: valor,
    }));
  }, []);

  const onBlur = useCallback((event) => {
    const { name, value } = event.target;
    setConfiguracao((prevState) => ({
      ...prevState,
      [name]: unformatPrice(value),
    }));
  }, []);

  const tabProps = useCallback((index) => {
    return {
      id: `full-width-tab-${index}`,
      "aria-controls": `full-width-tabpanel-${index}`,
    };
  }, []);

  const inserirValoresListaPagamentoPadrao = useCallback(
    (configuracaoPagamentosPadrao) => {
      const idsPagamentos =
        configuracaoPagamentosPadrao?.map((pagamento) => ({
          id: pagamento.meioPagamentoId,
        })) || [];
      setListaPagamentoPadrao(idsPagamentos);
    },
    [],
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: "GERAL",
        component: (
          <ConfiguracaoGeralTab
            setLoading={setLoading}
            configuracao={configuracao}
            onChange={onChange}
          />
        ),
      },
      {
        label: "CADASTRO",
        component: (
          <ConfiguracaoCadastroTab
            configuracao={configuracao}
            onChangeSwitch={onChangeSwitch}
            onChange={onChange}
          />
        ),
      },
      {
        label: "COMPRAS",
        component: (
          <ConfiguracaoComprasTab
            configuracao={configuracao}
            onChangeAutocomplete={onChangeAutocomplete}
            onChangeSwitch={onChangeSwitch}
          />
        ),
      },
      {
        label: "VENDAS",
        component: (
          <ConfiguracaoVendaTab
            configuracao={configuracao}
            onChangeAutocomplete={onChangeAutocomplete}
            onChangeSwitch={onChangeSwitch}
            onChange={onChange}
            dadosEmpresa={dadosEmpresa}
          />
        ),
      },
      {
        label: "FINANCEIRO",
        component: (
          <ConfiguracaoFinanceiroTab
            configuracao={configuracao}
            onChange={onChange}
            onBlur={onBlur}
            onChangeAutocomplete={onChangeAutocomplete}
            onChangeSwitch={onChangeSwitch}
          />
        ),
      },
      {
        label: "FISCAL",
        component: (
          <ConfiguracaoFiscalTab
            configuracao={configuracao}
            onChangeDate={onChangeDate}
            onBlur={onBlur}
            onChangeValor={onChangeValor}
          />
        ),
      },
      {
        label: "PDV",
        component: (
          <ConfiguracaoPdvTab
            configuracao={configuracao}
            setConfiguracao={setConfiguracao}
            listaPagamentoPadrao={listaPagamentoPadrao}
            setListaPagamentoPadrao={setListaPagamentoPadrao}
            onChangeAutocomplete={onChangeAutocomplete}
          />
        ),
      },
    ];

    if (dadosEmpresa?.moduloServico) {
      const posicaoMenuVendas = baseTabs.findIndex(
        (item) => item.label === "PDV",
      );
      if (posicaoMenuVendas !== -1) {
        baseTabs.splice(posicaoMenuVendas + 1, 0, {
          label: "SERVIÇOS",
          component: (
            <ConfiguracaoServicosTab
              configuracao={configuracao}
              onChange={onChange}
              onChangeSwitch={onChangeSwitch}
              onChangeRadio={onChangeRadio}
            />
          ),
        });
      }
    }

    if (dadosEmpresa?.whatsappSimples || dadosEmpresa?.whatsappAvancado) {
      baseTabs.push({
        label: "INTEGRAÇÕES",
        component: <IntegracoesTab />,
      });
    }

    baseTabs.push({
      label: "IMPRESSÃO",
      component: (
        <ConfiguracaoImpressaoTab
          configuracao={configuracao}
          setConfiguracao={setConfiguracao}
          onChange={onChange}
        />
      ),
    });

    return baseTabs;
  }, [
    configuracao,
    listaPagamentoPadrao,
    dadosEmpresa,
    onChange,
    onBlur,
    onChangeSwitch,
    onChangeAutocomplete,
    onChangeValor,
  ]);

  const handleTabChange = useCallback((e, value) => {
    e.persist();
    setAbaValue(value);
  }, []);

  return (
    <AddLayout
      title="Editar Configurações"
      onClickSalvar={handleSubmit}
      loading={loading}
      userLog={null}
      showUpdate={permissoesHelper.temPermisao("configuracoes-editar")}
      submitSimples
    >
      <Paper>
        <Tabs
          value={abaValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} {...tabProps(index)} />
          ))}
        </Tabs>
      </Paper>

      {tabs.map((tab, index) => (
        <TabPanel key={index} value={abaValue} index={index} className="mt-3">
          {abaValue === index ? tab.component : null}
        </TabPanel>
      ))}
    </AddLayout>
  );
};

export default Configuracoes;
